import { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  EnumOwnerType,
  EnumSurveyResponseAdminStatus,
  EnumTagEntityTypes,
  useSurveyResponseAdminEditMutation,
  useSurveyResponseByIdQuery,
  useSurveyResponseDeleteByIdMutation,
  useSurveyResponseReviewSubmissionMutation,
  useSurveyResponseUpdateAdminStatusMutation,
  useSurveyResponseUpdateTagsMutation,
  useTagManyQuery,
} from "gql/generated";
import { Combobox, useFilter } from "@ttc3k/trekker";
import { SurveyResponseModal } from "./SurveyResponseModal";
import { SurveyResponseHeader } from "./SurveyResponseHeader";
import { SurveyResponseBody } from "./SurveyResponseBody";
import { useLoggedInAs } from "shared/hooks";
import { getMultilingualString } from "shared/utils";
import type { BusinessAnswer } from "@shared/businessScope";

type SurveyResponseViewerProps = {
  isOpen: boolean;
  onClose: () => void;
  surveyResponseId: string | null;
  portal?: boolean;
  /** Submitter name in the header becomes a control that opens this person's profile. */
  onOpenPersonProfile?: (personId: string) => void;
  onDeleted?: () => void;
  onAdminStatusChange?: (surveyResponseId: string, adminStatus: EnumSurveyResponseAdminStatus) => void;
  onTagsChange?: (surveyResponseId: string, tags: string[]) => void;
  onReviewStatusChange?: (surveyResponseId: string, status: string) => void;
  navigation?: {
    onPrev: (() => void) | null;
    onNext: (() => void) | null;
  };
};

const ACCENT = {
  banner: "bg.accent.magenta.mid",
  closeBg: "bg.accent.magenta.dark",
  closeBgHover: "bg.accent.magenta.darkest",
};

export const SurveyResponseViewer = ({
  isOpen,
  onClose,
  surveyResponseId,
  portal,
  onOpenPersonProfile,
  onDeleted,
  onAdminStatusChange,
  onTagsChange,
  onReviewStatusChange,
  navigation,
}: SurveyResponseViewerProps) => {
  const { t } = useTranslation("people", {
    keyPrefix: "FORM_RESPONSE_MODAL",
  });
  const { t: tCore } = useTranslation("core", {
    keyPrefix: "MODAL_BUTTON_COPY",
  });
  const loggedInAs = useLoggedInAs();

  const { data, loading, error, refetch } = useSurveyResponseByIdQuery({
    variables: { id: surveyResponseId ?? "" },
    skip: !surveyResponseId,
  });

  const [reviewSubmission, { loading: reviewLoading }] =
    useSurveyResponseReviewSubmissionMutation();
  const [updateAdminStatus, { loading: statusLoading }] =
    useSurveyResponseUpdateAdminStatusMutation();
  const [deleteSurveyResponse, { loading: deleteLoading }] =
    useSurveyResponseDeleteByIdMutation();
  const [adminEdit, { loading: editLoading }] =
    useSurveyResponseAdminEditMutation();

  const [reviewNote, setReviewNote] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editedResponses, setEditedResponses] = useState<
    { questionData: any; response: string[]; byBusiness?: BusinessAnswer[] }[]
  >([]);

  const surveyResponse = data?.surveyResponseById as any;
  const isPendingReview = surveyResponse?.status === "PENDING_REVIEW";
  const isUnread = surveyResponse?.adminStatus === "UNREAD";

  const canEdit =
    loggedInAs?.type === EnumOwnerType.Organization ||
    loggedInAs?.type === EnumOwnerType.TnbInternal;

  // --- Footer tag selector ---
  const [updateTags, { loading: tagsLoading }] =
    useSurveyResponseUpdateTagsMutation();

  const { data: tagsData } = useTagManyQuery({
    variables: {
      filter: {
        entityTypes: [EnumTagEntityTypes.Person],
        ...(loggedInAs?.entityId
          ? { owner: { entityId: loggedInAs.entityId, type: EnumOwnerType.Organization } }
          : {}),
      },
      limit: 10000,
    },
    skip: !loggedInAs?.entityId,
  });

  const tagOptions = useMemo(() => {
    return (tagsData?.tagMany ?? []).map(tag => ({
      value: tag._id,
      label: getMultilingualString(tag.name),
    }));
  }, [tagsData]);

  const selectedTagIds = useMemo(() => {
    return (surveyResponse?.tags ?? []).map((t: any) =>
      typeof t === "string" ? t : t?.toString?.() ?? t,
    );
  }, [surveyResponse?.tags]);

  const { contains } = useFilter({ sensitivity: "base" });
  const { collection, filter: filterTags, set: setTagCollection } = Combobox.useCollection({
    initialItems: tagOptions,
    filter: contains,
  });

  useEffect(() => {
    setTagCollection(tagOptions);
  }, [tagOptions, setTagCollection]);

  const handleTagsChange = useCallback(
    async (newTagIds: string[]) => {
      if (!surveyResponseId) return;
      await updateTags({
        variables: { surveyResponseId, tags: newTagIds },
      });
      onTagsChange?.(surveyResponseId, newTagIds);
      await refetch();
    },
    [surveyResponseId, updateTags, refetch, onTagsChange],
  );

  const tagCombobox = tagOptions.length > 0 ? (
    <Combobox.Root
      value={selectedTagIds}
      collection={collection}
      multiple
      onValueChange={d => handleTagsChange(d.value)}
      onInputValueChange={d => filterTags(d.inputValue)}
      onOpenChange={d => {
        if (d.open) setTagCollection(tagOptions);
      }}
      placeholder={t("TAGS_PLACEHOLDER")}
      tagVisualVariant="accent.purple"
      tagModeVariant="dark"
      disabled={tagsLoading}
    >
      {collection.items.map(tag => (
        <Combobox.Item key={tag.value} item={tag} label={tag.label} />
      ))}
    </Combobox.Root>
  ) : null;

  const organizationId = useMemo(() => {
    if (loggedInAs?.type === EnumOwnerType.Organization)
      return loggedInAs.entityId;
    return surveyResponse?.ownerMetadata?.organization ?? undefined;
  }, [loggedInAs, surveyResponse?.ownerMetadata?.organization]);

  const handleStartEdit = useCallback(() => {
    if (!surveyResponse?.responses) return;
    setEditedResponses(
      surveyResponse.responses.map((r: any) => ({
        questionData: r.questionData,
        response: [...(r.response ?? [])],
        ...(r.byBusiness?.length ? { byBusiness: r.byBusiness } : {}),
      })),
    );
    setIsEditing(true);
  }, [surveyResponse?.responses]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditedResponses([]);
  }, []);

  const handleEditResponseChange = useCallback(
    (questionId: string, response: string[]) => {
      setEditedResponses(prev =>
        prev.map(r =>
          r.questionData._id === questionId ? { ...r, response } : r,
        ),
      );
    },
    [],
  );

  const handleSaveEdit = useCallback(async () => {
    if (!surveyResponseId) return;
    const strip = (obj: any): any => {
      if (Array.isArray(obj)) return obj.map(strip);
      if (obj && typeof obj === "object") {
        const out: any = {};
        for (const [k, v] of Object.entries(obj)) {
          if (k === "__typename") continue;
          out[k] = strip(v);
        }
        return out;
      }
      return obj;
    };
    const payload = editedResponses.map(r =>
      strip({
        questionData: {
          _id: r.questionData._id,
          isActive: r.questionData.isActive ?? true,
          name: r.questionData.name,
          options: r.questionData.options,
          owner: r.questionData.owner,
          required: r.questionData.required,
          type: r.questionData.type,
          whereaboutsSearchEntityType:
            r.questionData.whereaboutsSearchEntityType ?? undefined,
          parentQuestion: r.questionData.parentQuestion ?? undefined,
          businessScope: r.questionData.businessScope ?? undefined,
        },
        response: r.response,
        ...(r.byBusiness?.length ? { byBusiness: r.byBusiness } : {}),
      }),
    );
    try {
      await adminEdit({
        variables: { surveyResponseId, responses: payload },
      });
      await refetch();
      setIsEditing(false);
      setEditedResponses([]);
    } catch {
      // Error surfaced by Apollo
    }
  }, [surveyResponseId, editedResponses, adminEdit, refetch]);

  const handleMarkAs = useCallback(
    async (status: EnumSurveyResponseAdminStatus) => {
      if (!surveyResponseId) return;
      await updateAdminStatus({
        variables: { surveyResponseId, adminStatus: status },
      });
      onAdminStatusChange?.(surveyResponseId, status);
      await refetch();
      if (status === EnumSurveyResponseAdminStatus.Read) onClose();
    },
    [surveyResponseId, updateAdminStatus, refetch, onClose, onAdminStatusChange],
  );

  const handleReview = useCallback(
    async (decision: "APPROVED" | "REJECTED") => {
      if (!surveyResponseId) return;
      await reviewSubmission({
        variables: {
          surveyResponseId,
          decision,
          reviewNote: reviewNote || undefined,
        },
      });
      setReviewNote("");
      onReviewStatusChange?.(surveyResponseId, decision === "APPROVED" ? "SUBMITTED" : "REJECTED");
      await refetch();
    },
    [surveyResponseId, reviewSubmission, reviewNote, refetch, onReviewStatusChange],
  );

  const handleDelete = useCallback(async () => {
    if (!surveyResponseId) return;
    await deleteSurveyResponse({ variables: { surveyResponseId } });
    onClose();
    onDeleted?.();
  }, [surveyResponseId, deleteSurveyResponse, onClose, onDeleted]);

  return (
    <SurveyResponseModal
      isOpen={isOpen}
      onClose={onClose}
      portal={portal}
      loading={loading}
      error={error}
      accent={ACCENT}
      isNeutral={!isUnread}
      headerCard={
        <SurveyResponseHeader
          surveyResponse={surveyResponse}
          onOpenPersonProfile={onOpenPersonProfile}
        />
      }
      onDelete={isEditing ? undefined : handleDelete}
      deleteLoading={deleteLoading}
      deleteLabel={t("DELETE")}
      deleteDialogTitle={t("DELETE_CONFIRM_TITLE")}
      deleteDialogMessage={t("DELETE_CONFIRM_DESCRIPTION")}
      deleteDialogCancelLabel={tCore("CANCEL") ?? "Cancel"}
      primaryAction={{
        label: isUnread ? t("MARK_READ") : t("MARK_UNREAD"),
        onClick: () => handleMarkAs(isUnread ? EnumSurveyResponseAdminStatus.Read : EnumSurveyResponseAdminStatus.Unread),
        loading: statusLoading,
        disabled: statusLoading || isEditing,
      }}
      closeDisabled={isEditing}
      navigation={navigation}
      footerExtra={tagCombobox}
    >
      <SurveyResponseBody
        surveyResponse={surveyResponse}
        surveyResponseId={surveyResponseId}
        refetch={refetch}
        isPendingReview={isPendingReview}
        reviewNote={reviewNote}
        setReviewNote={setReviewNote}
        onReview={handleReview}
        reviewLoading={reviewLoading}
        canEdit={canEdit}
        isEditing={isEditing}
        editedResponses={editedResponses}
        onStartEdit={handleStartEdit}
        onCancelEdit={handleCancelEdit}
        onEditResponseChange={handleEditResponseChange}
        onSaveEdit={handleSaveEdit}
        editLoading={editLoading}
        organizationId={organizationId}
      />
    </SurveyResponseModal>
  );
};
